/**
 * KAN-773, KAN-790: ввод длительности в часах - число плюс единица (дн./ч.).
 *
 * Значение хранится в часах (целое 1..720). Единица - только отображение:
 * длительность, кратная 24, показывается в днях, пока пользователь не выбрал
 * единицу сам. Используется для задержки касания и срока задачи менеджеру в
 * редакторе лестницы (NudgeLadderEditor).
 */
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import {
  clampDelayHours,
  hoursFromInput,
  NUDGE_DELAY_MAX_H,
  unitForHours,
  type HoursUnit,
} from '@/lib/nudgeLadder';

const UNIT_OPTIONS: Array<{ value: HoursUnit; label: string }> = [
  { value: 'days', label: 'дн.' },
  { value: 'hours', label: 'ч.' },
];

interface HoursFieldProps {
  /** id числового поля; id селектора единицы = `${unitId}`. */
  id: string;
  unitId: string;
  testId?: string;
  unitTestId?: string;
  label: string;
  unitLabel: string;
  hours: number;
  onChange: (hours: number) => void;
  disabled?: boolean;
}

export function HoursField({
  id,
  unitId,
  testId,
  unitTestId,
  label,
  unitLabel,
  hours,
  onChange,
  disabled = false,
}: HoursFieldProps): JSX.Element {
  // Unit chosen by the user; without a choice it is derived from the hours.
  // The draft keeps a half-typed number (including an empty field) from
  // snapping back while editing.
  const [chosenUnit, setChosenUnit] = useState<HoursUnit | null>(null);
  const [draft, setDraft] = useState<string | null>(null);

  const unit = chosenUnit ?? unitForHours(hours);
  const shown = draft ?? String(unit === 'days' ? hours / 24 : hours);

  const handleInput = (text: string): void => {
    setDraft(text);
    const next = hoursFromInput(text, unit);
    if (next !== null) onChange(next);
  };

  const handleUnit = (next: HoursUnit): void => {
    setChosenUnit(next);
    setDraft(null);
    if (next !== 'days') return;
    // Switching to days rounds the stored hours to whole days (at least one).
    const rounded = clampDelayHours(Math.max(1, Math.round(hours / 24)) * 24);
    if (rounded !== null && rounded !== hours) onChange(rounded);
  };

  return (
    <div className="flex items-center gap-1">
      <Input
        id={id}
        data-testid={testId}
        aria-label={label}
        type="number"
        className="w-20"
        min={1}
        max={unit === 'days' ? NUDGE_DELAY_MAX_H / 24 : NUDGE_DELAY_MAX_H}
        step={1}
        value={shown}
        disabled={disabled}
        onChange={(e) => handleInput(e.target.value)}
        onBlur={() => setDraft(null)}
      />
      <NativeSelect
        id={unitId}
        data-testid={unitTestId}
        aria-label={unitLabel}
        className="h-10 w-20"
        options={UNIT_OPTIONS}
        value={unit}
        disabled={disabled}
        onChange={(e) => handleUnit(e.target.value as HoursUnit)}
      />
    </div>
  );
}
